import { GrpcCurrency } from '@m/types/grpc/GrpcTypes';

export enum EnforcementStatus {
  OK = 'OK',
  Notice = 'Notice',
  Violation = 'Violation',
}

export type EnforcementLookup = {
  coveredBySubscription: boolean;
  enforcementStatus: EnforcementStatus;
  imageUrl?: string; // undefined when searching by plate text
  isMember: boolean;
  plateText: string;
  specialistInstructions: string;
  user?: {
    firstName: string | null;
    isRegistered: boolean;
    lastName: string | null;
  };
};

export type EnforcementPlan = {
  fineAmount: GrpcCurrency;
  /** Whether this plan has any escalation configured (a cohort override or a time-based
   * default tier). Computed on both the RPC and REST list paths. */
  hasEscalationConfiguration?: boolean;
  minimumVisitDurationMinutes?: number;
  name: string;
  planId: string;
  versionDate: string;
};

/**
 * Wire shapes for GET /v2/enforcement/plans. This REST endpoint is a JSON passthrough of the
 * same `EnforcementPlan` protobuf message the Connect-RPC list returns (see
 * `EnforcementPlanControllerV2.listPlans` and `enforcement_plan_service.proto` in the site
 * repo) -- confirmed 2026-09-21; previously untyped on this path. `google.type.Money`'s int64
 * `units` is proto3-JSON-mapped to a decimal string, matching `GrpcCurrency`, so it's reused
 * here rather than the RPC path's bigint-typed generated `Money`.
 */
export type RestChargeAmounts = {
  fee: GrpcCurrency;
  fine: GrpcCurrency;
};

export type RestEscalationTier = {
  amounts: RestChargeAmounts;
  daysElapsed: number;
};

export type RestEscalationPlan = {
  base: RestChargeAmounts;
  escalations: RestEscalationTier[];
};

/** `google.protobuf.BoolValue` fields: unset (not just `false`) means "matches either value". */
export type RestCohortDimensions = {
  isFirstViolation?: boolean | null;
  isMember?: boolean | null;
};

export type RestCohortEntry = {
  dimensions: RestCohortDimensions;
  escalationPlan?: RestEscalationPlan;
  name: string;
};

export type RestEnforcementPlan = EnforcementPlan & {
  cohorts: RestCohortEntry[];
  currencyCode: string;
  /** Always populated on responses per the proto's own field comment. */
  defaultEscalationPlan: RestEscalationPlan;
};

export type ZoneEnforcementPlanDetails = {
  collectionsEnabled?: boolean;
  enforcementPlanId: string;
  enforcementStartDate: number;
  fineAmount: number;
  isPaused: boolean;
  mailNotices: boolean;
  versionDate: string;
};
